import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { coursesApi, documentationApi, paymentsApi } from '../../../services/api'
import Button from '../../../components/ui/Button'
import Card, { CardBody } from '../../../components/ui/Card'
import { Field, Input, Select, Textarea } from '../../../components/ui/Field'
import PageHeader from '../../../components/dashboard/PageHeader'

const SIWES_DRAFT_KEY = 'siwesDraft'

function isGenerationIncomplete (doc) {
  if (!doc) return false
  return doc.generationStatus === 'IN_PROGRESS' || doc.generationStatus === 'FAILED'
}

function readSiwesDraft () {
  try {
    const raw = sessionStorage.getItem(SIWES_DRAFT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeSiwesDraft (draft) {
  sessionStorage.setItem(SIWES_DRAFT_KEY, JSON.stringify(draft))
}

function clearSiwesDraft () {
  sessionStorage.removeItem(SIWES_DRAFT_KEY)
}

const defaultFormState = () => ({
  courseId: '',
  title: 'SIWES Report',
  company: '',
  duration: '',
  description: ''
})

const SiwesReport = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const paymentSuccess = searchParams.get('paymentSuccess') === '1'

  const [courses, setCourses] = useState([])
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [courseId, setCourseId] = useState('')
  const [title, setTitle] = useState('SIWES Report')
  const [company, setCompany] = useState('')
  const [duration, setDuration] = useState('')
  const [description, setDescription] = useState('')
  const [genLoading, setGenLoading] = useState(false)
  const [genProgress, setGenProgress] = useState(null)
  const [error, setError] = useState('')
  const [hasPaid, setHasPaid] = useState(false)
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false)

  const incompleteDoc = useMemo(
    () => docs.find((d) => isGenerationIncomplete(d)) || null,
    [docs]
  )

  const applyDraft = (draft) => {
    if (!draft) return
    if (draft.courseId) setCourseId(draft.courseId)
    if (draft.title) setTitle(draft.title)
    if (draft.company) setCompany(draft.company)
    if (draft.duration) setDuration(draft.duration)
    if (draft.description) setDescription(draft.description)
  }

  const resetForm = () => {
    const defaults = defaultFormState()
    setCourseId(defaults.courseId)
    setTitle(defaults.title)
    setCompany(defaults.company)
    setDuration(defaults.duration)
    setDescription(defaults.description)
  }

  const load = async () => {
    setLoading(true)
    try {
      const [coursesRes, docsRes, paymentsRes] = await Promise.all([
        coursesApi.list(),
        documentationApi.list({ standalone: 'siwes' }),
        paymentsApi.mine({ paymentStatus: 'successful', limit: 50 })
      ])
      setCourses(coursesRes?.data?.data || [])
      const allDocs = docsRes?.data?.data || []
      setDocs(allDocs)
      const payments = paymentsRes?.data?.data || []
      const unusedSiwesPayment = payments.some(
        (p) =>
          p?.productType === 'SIWES_REPORT_GENERATION' &&
          p?.paymentStatus === 'successful' &&
          !p?.project &&
          !p?.documentation
      )
      setHasPaid(unusedSiwesPayment)

      const incomplete = allDocs.find((d) => isGenerationIncomplete(d))
      if (incomplete) {
        setCourseId(incomplete.courseId?._id || incomplete.courseId || '')
        setTitle(incomplete.title || 'SIWES Report')
      }
    } catch {
      setError('Could not load SIWES report data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    if (!paymentSuccess) return
    const draft = readSiwesDraft()
    if (draft) applyDraft(draft)
    setShowPaymentSuccess(true)
    setSearchParams({}, { replace: true })
  }, [paymentSuccess, setSearchParams])

  const canGenerate = useMemo(() => {
    if (incompleteDoc) return !!courseId
    return !!courseId && title.trim().length >= 3 && hasPaid
  }, [incompleteDoc, courseId, title, hasPaid])

  const handlePay = () => {
    writeSiwesDraft({ courseId, title, company, duration, description })
    window.location.href = '/pricing?generationType=SIWES_REPORT'
  }

  const handleGenerate = async () => {
    if (!courseId) {
      setError('Select your department/course.')
      return
    }
    setGenLoading(true)
    setGenProgress({ percent: 0, batchLabel: 'Starting…' })
    setError('')
    try {
      const result = await documentationApi.generateStream(
        {
          generationType: 'SIWES_REPORT',
          courseId,
          title: title.trim(),
          company: company.trim(),
          duration: duration.trim(),
          description: description.trim()
        },
        (evt) => {
          if (evt.type === 'progress') {
            setGenProgress({
              percent: evt.percent ?? 0,
              batchLabel: evt.batchLabel || ''
            })
          }
        }
      )
      if (result) {
        setGenProgress(null)
        clearSiwesDraft()
        setShowPaymentSuccess(false)
        resetForm()
        await load()
      }
    } catch (e) {
      setGenProgress(null)
      if (e?.response?.status === 402 || e?.code === 'PAYMENT_REQUIRED') {
        setError(e?.response?.data?.message || e.message || 'Payment required')
        handlePay()
        return
      }
      setError(e?.response?.data?.message || e.message || 'Generation failed')
    } finally {
      setGenLoading(false)
    }
  }

  if (loading) return <p className="text-slate-600 p-4">Loading SIWES report…</p>

  const showNewReportForm = !incompleteDoc

  return (
    <div className="max-w-2xl">
      <PageHeader
        eyebrow="SIWES"
        title="Internship report"
        description="Generate your SIWES report independently — no project required. Uses your course template."
      />

      {showPaymentSuccess ? (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          Payment successful — review your details below and generate your report.
        </div>
      ) : null}

      {docs.length > 0 ? (
        <Card className="mb-6 shadow-soft">
          <CardBody className="space-y-3">
            <h2 className="font-semibold text-slate-900">Your SIWES reports</h2>
            <ul className="space-y-2">
              {docs.map((d) => (
                <li
                  key={d._id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{d.title || 'SIWES Report'}</p>
                    <p className="text-xs text-slate-500">
                      {d.generationStatus === 'COMPLETE'
                        ? 'Complete'
                        : d.generationStatus === 'IN_PROGRESS'
                          ? 'In progress'
                          : d.generationStatus === 'FAILED'
                            ? 'Paused'
                            : d.generationStatus || 'Unknown'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {d.generationStatus === 'COMPLETE' ? (
                      <>
                        <Button size="sm" onClick={() => navigate(`/dashboard/project/full/${d._id}`)}>
                          Open
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigate(`/dashboard/editor?docId=${encodeURIComponent(String(d._id))}`)
                          }
                        >
                          Edit
                        </Button>
                      </>
                    ) : isGenerationIncomplete(d) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCourseId(d.courseId?._id || d.courseId || '')
                          setTitle(d.title || 'SIWES Report')
                        }}
                      >
                        Resume
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {showNewReportForm ? (
        <Card className="shadow-soft">
          <CardBody className="space-y-4">
            <h2 className="font-semibold text-slate-900">
              {docs.length > 0 ? 'Generate a new SIWES report' : 'New SIWES report'}
            </h2>
            <Field label="Department / Course" hint="Required for template selection.">
              <Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="">Select department / course</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Report title">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Company / Organisation (optional)">
              <Input value={company} onChange={(e) => setCompany(e.target.value)} />
            </Field>
            <Field label="Duration (optional)">
              <Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 6 months" />
            </Field>
            <Field label="Additional notes (optional)">
              <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>

            {genLoading && genProgress ? (
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-medium">{genProgress.batchLabel}</p>
                <div className="mt-2 h-2 rounded-full bg-slate-200">
                  <div
                    className="h-2 rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${Math.min(100, genProgress.percent)}%` }}
                  />
                </div>
              </div>
            ) : null}

            {error ? <p className="text-sm text-red-700">{error}</p> : null}

            <div className="flex flex-wrap gap-2">
              {!hasPaid ? (
                <Button onClick={handlePay}>
                  {docs.length > 0 ? 'Pay for another SIWES report' : 'Pay for SIWES report'}
                </Button>
              ) : (
                <Button loading={genLoading} disabled={!canGenerate || genLoading} onClick={handleGenerate}>
                  {genLoading ? 'Generating…' : 'Generate SIWES report'}
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="shadow-soft">
          <CardBody className="space-y-4">
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              Generation paused — click Resume to continue from section {(incompleteDoc.chapters || []).length}
              {typeof incompleteDoc.generationTotalSections === 'number'
                ? ` of ${incompleteDoc.generationTotalSections}`
                : ''}.
            </p>

            <Field label="Department / Course" hint="Required for template selection.">
              <Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="">Select department / course</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>

            {genLoading && genProgress ? (
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-medium">{genProgress.batchLabel}</p>
                <div className="mt-2 h-2 rounded-full bg-slate-200">
                  <div
                    className="h-2 rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${Math.min(100, genProgress.percent)}%` }}
                  />
                </div>
              </div>
            ) : null}

            {error ? <p className="text-sm text-red-700">{error}</p> : null}

            <Button loading={genLoading} disabled={!canGenerate || genLoading} onClick={handleGenerate}>
              {genLoading ? 'Generating…' : 'Resume generation'}
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default SiwesReport
